import { Suspense, lazy } from "react";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { ThemeProvider } from "./hooks/ui/useTheme";
import { AccessibilityProvider } from "./contexts/AccessibilityContext";
import { initWebVitals } from "./lib/performance";
import PerformanceMonitor from "./components/dev/PerformanceMonitor";


// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Features = lazy(() => import("./pages/Features"));
const About = lazy(() => import("./pages/About"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Security = lazy(() => import("./pages/Security"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const DataProcessing = lazy(() => import("./pages/DataProcessing"));
const AIPoweredKitchenSoftware = lazy(() => import("./pages/AIPoweredKitchenSoftware"));
const AIRevolutionBlog = lazy(() => import("./pages/AIRevolutionBlog"));
const AIvsTraditionalComparison = lazy(() => import("./pages/AIvsTraditionalComparison"));
const AIKitchenSoftwareNZ = lazy(() => import("./pages/AIKitchenSoftwareNZ"));
const AIKitchenSoftwareAU = lazy(() => import("./pages/AIKitchenSoftwareAU"));
const VisualRegressionTest = lazy(() => import("./pages/VisualRegressionTest"));
const ThemeTest = lazy(() => import("./components/common/ThemeTest"));
const WCAGTest = lazy(() => import("./components/common/WCAGTest"));
// Future feature pages moved to docs/future-features/ for reference

const queryClient = new QueryClient();

// Initialize web vitals tracking
if (typeof window !== 'undefined') {
  initWebVitals();
}

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <AccessibilityProvider>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <TooltipProvider>
            <Toaster />
            <SonnerToaster />
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
          {/* Skip navigation links for keyboard users */}
          <div className="sr-only focus-within:not-sr-only focus-within:fixed focus-within:top-4 focus-within:left-4 focus-within:z-50 focus-within:flex focus-within:gap-2">
            <a href="#main-content" className="skip-link" tabIndex={1}>
              Skip to main content
            </a>
            <a href="#main-navigation" className="skip-link" tabIndex={2}>
              Skip to navigation
            </a>
            <a href="#footer" className="skip-link" tabIndex={3}>
              Skip to footer
            </a>
          </div>
          <Suspense fallback={
            <div className="min-h-svh flex items-center justify-center">
              <div className="rounded-full h-8 w-8 border-b-2 border-quotable-coral"></div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/features" element={<Features />} />
              <Route path="/about" element={<About />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/security" element={<Security />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/data-processing" element={<DataProcessing />} />
              <Route path="/ai-powered-kitchen-software" element={<AIPoweredKitchenSoftware />} />
              <Route path="/blog/ai-revolutionizing-kitchen-cabinet-estimation" element={<AIRevolutionBlog />} />
              <Route path="/blog/ai-powered-vs-traditional-kitchen-estimation" element={<AIvsTraditionalComparison />} />
              <Route path="/ai-kitchen-software-new-zealand" element={<AIKitchenSoftwareNZ />} />
              <Route path="/ai-kitchen-software-australia" element={<AIKitchenSoftwareAU />} />
              <Route path="/visual-regression-test" element={<VisualRegressionTest />} />
              <Route path="/theme-test" element={<ThemeTest />} />
              <Route path="/wcag-test" element={<WCAGTest />} />
              {/* Future feature routes moved to docs/future-features/ for reference */}
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>

          {/* Development performance monitor */}
          <PerformanceMonitor />
        </BrowserRouter>
            </TooltipProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </AccessibilityProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
